// js/common/modal.js

let activeModal = null;
let lastFocusedElement = null;
let isAnimating = false;
let isDocumentEventSet = false;

let duration = 300;

const initializedOpenButtons = new WeakSet();
const initializedModals = new WeakSet();

const defaultOptions = {
  openSelector: ".js-modal-open",
  modalSelector: ".js-modal",
  closeSelector: ".js-modal-close",
  duration: 300,
  bodyClass: "is-modal-open",
  scrollTargetSelector: ".js-modal-scroll, .p-member-modal__inner",
};

let currentOptions = { ...defaultOptions };

export function initModal(options = {}) {
  currentOptions = {
    ...defaultOptions,
    ...options,
  };

  duration = currentOptions.duration;

  const openButtons = document.querySelectorAll(currentOptions.openSelector);
  const modals = document.querySelectorAll(currentOptions.modalSelector);

  if (openButtons.length === 0 || modals.length === 0) return;

  openButtons.forEach((button) => {
    if (initializedOpenButtons.has(button)) return;

    initializedOpenButtons.add(button);

    button.addEventListener("click", (event) => {
      event.preventDefault();

      const targetId = button.dataset.modalTarget;

      if (!targetId) return;

      openModalById(targetId, button);
    });
  });

  modals.forEach((modal) => {
    if (initializedModals.has(modal)) return;

    initializedModals.add(modal);

    setupModal(modal);
  });

  setupDocumentEvents();
}

export function openModalById(targetId, trigger = null) {
  const modal = document.getElementById(targetId);

  if (!modal) return;

  openModal(modal, trigger);
}

export function openModal(modal, trigger = null, options = {}) {
  if (!modal || isAnimating) return;

  const { keepLastFocus = false } = options;

  if (activeModal && activeModal !== modal) {
    closeModal(activeModal, {
      returnFocus: false,
      immediate: true,
      keepBodyClass: true,
      keepLastFocus: true,
    });
  }

  activeModal = modal;

  if (!keepLastFocus) {
    lastFocusedElement = trigger || document.activeElement;
  }

  showModalElement(modal);

  modal.setAttribute("aria-hidden", "false");
  lockBodyScroll();

  requestAnimationFrame(() => {
    resetModalScroll(modal);
    modal.classList.add("is-open");
    focusFirstElement(modal);
  });
}

export function closeActiveModal(options = {}) {
  if (!activeModal) return;

  closeModal(activeModal, options);
}

export function closeModal(modal, options = {}) {
  if (!modal || isAnimating) return;

  const {
    returnFocus = true,
    immediate = false,
    keepBodyClass = false,
    keepLastFocus = false,
  } = options;

  const returnFocusElement = lastFocusedElement;

  if (modal.contains(document.activeElement)) {
    if (
      returnFocus &&
      returnFocusElement &&
      typeof returnFocusElement.focus === "function"
    ) {
      returnFocusElement.focus({ preventScroll: true });
    } else {
      document.activeElement.blur();
    }
  }

  modal.classList.remove("is-open");

  if (immediate) {
    hideModalElement(modal);
    modal.setAttribute("aria-hidden", "true");

    if (!keepBodyClass) {
      unlockBodyScroll();
    }

    if (activeModal === modal) {
      activeModal = null;
    }

    if (!keepLastFocus && !keepBodyClass) {
      lastFocusedElement = null;
    }

    return;
  }

  isAnimating = true;

  setTimeout(() => {
    hideModalElement(modal);
    modal.setAttribute("aria-hidden", "true");

    if (!keepBodyClass) {
      unlockBodyScroll();
    }

    if (activeModal === modal) {
      activeModal = null;
    }

    if (!keepLastFocus && !keepBodyClass) {
      lastFocusedElement = null;
    }

    isAnimating = false;
  }, duration);
}

export function switchModalById(targetId) {
  if (!activeModal || !targetId) return;

  const nextModal = document.getElementById(targetId);

  if (!nextModal || nextModal === activeModal) return;

  const returnFocusElement = lastFocusedElement;
  const currentModal = activeModal;

  closeModal(currentModal, {
    returnFocus: false,
    immediate: true,
    keepBodyClass: true,
    keepLastFocus: true,
  });

  lastFocusedElement = returnFocusElement;

  openModal(nextModal, null, {
    keepLastFocus: true,
  });
}

function setupModal(modal) {
  const closeButtons = modal.querySelectorAll(currentOptions.closeSelector);

  closeButtons.forEach((button) => {
    button.addEventListener("click", () => {
      closeModal(modal);
    });
  });

  modal.addEventListener("cancel", (event) => {
    event.preventDefault();

    if (!activeModal) return;

    closeModal(activeModal);
  });

  modal.addEventListener("click", (event) => {
    if (event.target !== modal) return;

    const rect = modal.getBoundingClientRect();

    const isBackdropClick =
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;

    if (!isBackdropClick) return;

    closeModal(modal);
  });

  modal.setAttribute("aria-hidden", "true");
  hideModalElement(modal);
}

function setupDocumentEvents() {
  if (isDocumentEventSet) return;

  isDocumentEventSet = true;

  document.addEventListener("keydown", (event) => {
    if (!activeModal) return;

    if (event.key === "Escape") {
      event.preventDefault();
      closeModal(activeModal);
      return;
    }

    if (event.key === "Tab") {
      keepFocusInModal(event, activeModal);
    }
  });
}

function showModalElement(modal) {
  modal.hidden = false;

  if (typeof modal.showModal === "function") {
    if (!modal.open) {
      modal.showModal();
    }
  }
}

function hideModalElement(modal) {
  if (typeof modal.close === "function") {
    if (modal.open) {
      modal.close();
    }
  }

  modal.hidden = true;
}

function lockBodyScroll() {
  document.documentElement.classList.add(currentOptions.bodyClass);
  document.body.classList.add(currentOptions.bodyClass);
}

function unlockBodyScroll() {
  document.documentElement.classList.remove(currentOptions.bodyClass);
  document.body.classList.remove(currentOptions.bodyClass);
}

function resetModalScroll(modal) {
  modal.scrollTop = 0;

  const scrollTargets = modal.querySelectorAll(
    currentOptions.scrollTargetSelector,
  );

  scrollTargets.forEach((scrollTarget) => {
    scrollTarget.scrollTop = 0;
  });
}

function focusFirstElement(modal) {
  const focusableElements = getFocusableElements(modal);

  if (focusableElements.length > 0) {
    focusableElements[0].focus({ preventScroll: true });
    return;
  }

  modal.setAttribute("tabindex", "-1");
  modal.focus({ preventScroll: true });
}

function keepFocusInModal(event, modal) {
  const focusableElements = getFocusableElements(modal);

  if (focusableElements.length === 0) {
    event.preventDefault();
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus({ preventScroll: true });
  }

  if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus({ preventScroll: true });
  }
}

function getFocusableElements(modal) {
  return Array.from(
    modal.querySelectorAll(
      [
        "a[href]",
        "button:not([disabled])",
        "input:not([disabled])",
        "textarea:not([disabled])",
        "select:not([disabled])",
        "[tabindex]:not([tabindex='-1'])",
      ].join(","),
    ),
  ).filter((element) => {
    return element.offsetParent !== null || element === document.activeElement;
  });
}
